import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type SmsTriggerType = 'days_before_due' | 'days_after_due' | 'on_event' | 'manual';
export type SmsStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'undelivered';

export type SmsTemplate = {
  id: string;
  name: string;
  bodyUz: string;
  bodyRu: string | null;
  bodyEn: string | null;
  active: boolean;
  moderationStatus: 'pending' | 'approved' | 'rejected';
};

export type SmsRule = {
  id: string;
  name: string;
  templateId: string;
  triggerType: SmsTriggerType;
  daysOffset: number | null;
  eventCode: string | null;
  active: boolean;
  critical: boolean;
  template?: { id: string; name: string };
};

export type SmsMessage = {
  id: string;
  customerId: string;
  templateId: string;
  phone: string;
  renderedBody: string;
  status: SmsStatus;
  provider: string | null;
  providerMessageId: string | null;
  cost: string | null;
  failureReason: string | null;
  retryCount: number;
  scheduledAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  template?: { id: string; name: string };
};

export function useSmsTemplates() {
  return useQuery({
    queryKey: ['sms-templates'],
    queryFn: async () => {
      const res = await api.get<{ data: SmsTemplate[] }>('/sms-templates');
      return res.data.data;
    },
  });
}

export function useCreateSmsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<SmsTemplate>) => {
      const res = await api.post<{ data: SmsTemplate }>('/sms-templates', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-templates'] });
    },
  });
}

export function useUpdateSmsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<SmsTemplate> }) => {
      const res = await api.patch<{ data: SmsTemplate }>(`/sms-templates/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-templates'] });
    },
  });
}

export function useDeleteSmsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sms-templates/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-templates'] });
    },
  });
}

export function useSmsRules() {
  return useQuery({
    queryKey: ['sms-rules'],
    queryFn: async () => {
      const res = await api.get<{ data: SmsRule[] }>('/sms-rules');
      return res.data.data;
    },
  });
}

export function useCreateSmsRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<SmsRule>) => {
      const res = await api.post<{ data: SmsRule }>('/sms-rules', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-rules'] });
    },
  });
}

export function useUpdateSmsRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<SmsRule> }) => {
      const res = await api.patch<{ data: SmsRule }>(`/sms-rules/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['sms-rules'] }); },
  });
}

export function useDeleteSmsRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/sms-rules/${id}`); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['sms-rules'] }); },
  });
}

export function useSmsRuleDryRun() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{
        data: { recipientCount: number; targetDate?: string; sample: unknown[]; note?: string };
      }>(`/sms-rules/${id}/dry-run`);
      return res.data.data;
    },
  });
}

export function useSmsMessages(params?: { status?: SmsStatus; customerId?: string }) {
  return useQuery({
    queryKey: ['sms-messages', params],
    queryFn: async () => {
      const res = await api.get<{ data: SmsMessage[] }>('/sms-messages', { params });
      return res.data.data;
    },
  });
}

export function useSmsBroadcastSend() {
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      audience: { projectIds?: string[]; contractStatuses?: string[]; tag?: string };
    }) => {
      const res = await api.post<{ data: { queued: number } }>('/sms-broadcasts', input);
      return res.data.data;
    },
  });
}

export function useSmsBroadcastDryRun() {
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      audience: { projectIds?: string[]; contractStatuses?: string[]; tag?: string };
    }) => {
      const res = await api.post<{ data: { recipientCount: number; sample: unknown[] } }>(
        '/sms-broadcasts/dry-run',
        input,
      );
      return res.data.data;
    },
  });
}
